import React, { useMemo, useState } from 'react';
import { createSelector } from '@reduxjs/toolkit';

import { useSelector } from 'react-redux';
import { Row, Col, Collapse } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { ApplicationQuestionnaireAssessmentRelated, ApplicationQuestionnaireListCategory } from '../../api/openapi';

import QuestionnaireDetailSubcategoryRow from './QuestionnaireDetailSubcategoryRow';
import QuestionnaireDetailQuestionsTable from './QuestionnaireDetailQuestionsTable';
import { RootState } from '../../store/rootReducer';

interface Props {
  questionnaireId: string;
  category: ApplicationQuestionnaireListCategory;
}

const assessmentsSelector = () => {
  return createSelector(
    (state: RootState) => state.questionnaire.applicationQuestionnaire?.assessments || [],
    (_: RootState, categoryId: string) => categoryId,
    (assessments: Array<ApplicationQuestionnaireAssessmentRelated>, categoryId) =>
      assessments
        .map((a) => ({
          ...a,
          categories: a.categories.filter((c) => c.categoryId === categoryId),
        }))
        .filter((a) => a.categories.length > 0)
  );
};

const QuestionnaireDetailCategoryRow: React.FC<Props> = ({ category, questionnaireId }: Props) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation(['questionnaireDetail', 'common']);

  const applicationQuestionnaire = useSelector((state: RootState) => state.questionnaire.applicationQuestionnaire);
  const assessmentsMemo = useMemo(assessmentsSelector, []);
  const assessments = useSelector((state: RootState) => assessmentsMemo(state, category.id));

  return (
    <>
      <div className="border-bottom border-top border border-3 p-3">
        <Row onClick={() => setOpen(!open)}>
          <Col>
            <h5 className="pt-2">
              <strong>{category.name}</strong>
            </h5>
          </Col>
          <Col md={2} className="text-center inline-block">
            {applicationQuestionnaire?.status === 'PUBLISHED' ? (
              <>
                {assessments.map((a) =>
                  a.categories.map((c) => (
                    <div key={`${a.id}${c.id}`}>
                      {c.approved === true ? (
                        <Row>
                          <Col md={8}>
                            {t('assessed')}
                            <br />
                            <FontAwesomeIcon icon={faCheckCircle} style={{ color: 'green' }} />
                          </Col>
                          <Col md={4}>
                            <FontAwesomeIcon icon={!open ? faChevronDown : faChevronUp} size="1x" className="ml-2 mt-3" />
                          </Col>
                        </Row>
                      ) : (
                        <Row>
                          <Col md={8}>
                            {t('notAssessed')}
                            <br />
                            <FontAwesomeIcon icon={faCheckCircle} style={{ color: 'gray' }} />
                          </Col>
                          <Col md={4}>
                            <FontAwesomeIcon icon={!open ? faChevronDown : faChevronUp} size="1x" className="ml-2 mt-3" />
                          </Col>
                        </Row>
                      )}
                    </div>
                  ))
                )}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={!open ? faChevronDown : faChevronUp} size="1x" className="float-right mr-3 mt-3" />
              </>
            )}
          </Col>
        </Row>
      </div>

      <Collapse in={open}>
        <div>
          {category.subcategories.map((subcategory) => (
            <QuestionnaireDetailSubcategoryRow key={subcategory.id} questionnaireId={questionnaireId} categoryId={category.id} subcategory={subcategory} />
          ))}

          <QuestionnaireDetailQuestionsTable categoryId={category.id} subcategoryId={undefined} />
        </div>
      </Collapse>
    </>
  );
};

export default QuestionnaireDetailCategoryRow;
